import React from 'react';

// Module icons — clean, modern stroke-based line icons (no emoji), used by the
// Hotel Workspace launcher cards and the left rail nav. One shared set keyed
// by the same module id used throughout App.js (paletteModuleDefs[].key /
// the launcher card's own `id`), so both surfaces stay visually consistent.

const base = { fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };

function Svg({ size, strokeWidth, color, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" stroke={color} strokeWidth={strokeWidth} {...base}>
      {children}
    </svg>
  );
}

// 8-point radial tick pattern, reused by the two "settings"-family icons so
// their teeth/notches line up exactly.
const RAY_DIRS = [
  [0, -1], [0.707, -0.707], [1, 0], [0.707, 0.707],
  [0, 1], [-0.707, 0.707], [-1, 0], [-0.707, -0.707],
];
function rays(rInner, rOuter) {
  return RAY_DIRS.map(([dx, dy], i) => (
    <line key={i} x1={12 + dx * rInner} y1={12 + dy * rInner} x2={12 + dx * rOuter} y2={12 + dy * rOuter} />
  ));
}

const ICONS = {
  'front-office': (p) => (
    <Svg {...p}>
      <circle cx="13" cy="5" r="1.8" fill={p.color} stroke="none" />
      <path d="M13 6.8L10 13H16Z" fill={p.color} stroke="none" />
      <rect x="2" y="13" width="20" height="7" />
      <rect x="3.6" y="10.6" width="3" height="1.8" />
      <path d="M3.9 10.6a1.15 0.9 0 0 1 2.4 0" />
    </Svg>
  ),
  'bar-management': (p) => (
    <Svg {...p}>
      <path d="M5 4L19 4L12 13Z" />
      <path d="M12 13v6.5" />
      <path d="M8.5 19.5h7" />
      <path d="M6.3 4.3L4.3 2.3" />
      <circle cx="3.7" cy="1.7" r="1.1" fill={p.color} stroke="none" />
    </Svg>
  ),
  kot: (p) => (
    <Svg {...p}>
      <path d="M6 13a6 6 0 0 1 12 0" />
      <ellipse cx="12" cy="13" rx="7" ry="1.4" />
      <line x1="12" y1="7" x2="12" y2="5.5" />
      <circle cx="12" cy="5" r="0.7" fill={p.color} stroke="none" />
      <path d="M4 17q2 -2.2 4 0q2 -2.2 4 0q2 -2.2 4 0q2 -2.2 4 0" />
      <path d="M4 17Q4 20 8 20L16 20Q20 20 20 17" />
    </Svg>
  ),
  'food-inventory': (p) => (
    <Svg {...p}>
      <path d="M4 8l8-5 8 5" />
      <rect x="4" y="8" width="16" height="12" />
      <path d="M12 8v12" />
    </Svg>
  ),
  stores: (p) => (
    <Svg {...p}>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <rect x="6" y="5.5" width="4" height="3.5" />
      <rect x="13" y="11.5" width="5" height="3.5" />
      <rect x="7" y="17.5" width="3" height="3.5" />
    </Svg>
  ),
  banquet: (p) => (
    <Svg {...p}>
      <circle cx="7" cy="7.3" r="1.6" />
      <circle cx="12" cy="6.6" r="1.7" />
      <circle cx="17" cy="7.3" r="1.6" />
      <line x1="7" y1="8.9" x2="7" y2="15" />
      <line x1="12" y1="8.3" x2="12" y2="15" />
      <line x1="17" y1="8.9" x2="17" y2="15" />
      <path d="M9 15a3 2.2 0 0 1 6 0" />
      <circle cx="12" cy="12.4" r="0.55" fill={p.color} stroke="none" />
      <line x1="2" y1="15" x2="22" y2="15" />
      <path d="M2 15q2.5 3 5 0q2.5 3 5 0q2.5 3 5 0q2.5 3 5 0" />
      <line x1="5" y1="18.3" x2="5" y2="20.5" />
      <line x1="19" y1="18.3" x2="19" y2="20.5" />
    </Svg>
  ),
  housekeeping: (p) => (
    <Svg {...p}>
      <circle cx="9" cy="4.3" r="1.9" fill={p.color} stroke="none" />
      <path d="M9 6.2L6.2 15.3H11.8Z" fill={p.color} stroke="none" />
      <line x1="7.4" y1="15.3" x2="7.4" y2="19" />
      <line x1="10.6" y1="15.3" x2="10.6" y2="19" />
      <line x1="9.5" y1="9" x2="17" y2="16.5" />
      <line x1="17" y1="16.5" x2="19.2" y2="15" />
      <line x1="17" y1="16.5" x2="19.6" y2="17" />
      <line x1="17" y1="16.5" x2="18.8" y2="19" />
    </Svg>
  ),
  engineering: (p) => (
    <Svg {...p}>
      <circle cx="6.3" cy="17.7" r="2.3" />
      <circle cx="17.7" cy="6.3" r="2.3" />
      <path d="M8 16l8-8" />
    </Svg>
  ),
  accounting: (p) => (
    <Svg {...p}>
      <rect x="2" y="5" width="7" height="15" />
      <line x1="5.5" y1="5" x2="5.5" y2="20" />
      <rect x="11" y="3" width="11" height="18" rx="1.5" />
      <rect x="13" y="5" width="7" height="3" />
      <rect x="13" y="10" width="2" height="2" />
      <rect x="16.5" y="10" width="2" height="2" />
      <rect x="13" y="13.5" width="2" height="2" />
      <circle cx="18.5" cy="15.5" r="1.6" />
      <path d="M18.5 15.5L18.5 13.9" />
      <path d="M18.5 15.5L19.9 16.3" />
    </Svg>
  ),
  'hotel-config': (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3.2" />
      {rays(6.2, 9)}
    </Svg>
  ),
  'ai-functions': (p) => (
    <Svg {...p}>
      <path d="M12 3l2 7 7 2-7 2-2 7-2-7-7-2 7-2z" />
    </Svg>
  ),
  expense: (p) => (
    <Svg {...p}>
      <polyline points="3,17 9,11 13,15 21,5" />
      <polyline points="15,5 21,5 21,11" />
    </Svg>
  ),
  subscription: (p) => (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <rect x="6" y="13.2" width="4" height="2.6" rx="0.5" />
    </Svg>
  ),
  settings: (p) => (
    <Svg {...p}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <circle cx="9" cy="7" r="2" fill={p.color} stroke="none" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <circle cx="15" cy="12" r="2" fill={p.color} stroke="none" />
      <line x1="4" y1="17" x2="20" y2="17" />
      <circle cx="7" cy="17" r="2" fill={p.color} stroke="none" />
    </Svg>
  ),
};

export function ModuleIcon({ id, size = 22, strokeWidth = 1.6, color = 'currentColor' }) {
  const render = ICONS[id];
  if (!render) return null;
  return render({ size, strokeWidth, color });
}

export default ModuleIcon;
